import Vue from 'vue';
import Vuex from 'vuex';
import router from '@/router';
import {
  CLIENT_MENU,
  buildMenuList,
  buildFallbackMenuList,
  menuIsValidTwoLevel,
  menuListIsValid
} from '@/config/menu';

Vue.use(Vuex);

// 从菜单列表中提取权限
function extractPermissionsFromMenu(menuList) {
  const permissions = [];
  
  function traverseMenu(menu) {
    // 添加当前菜单的权限
    permissions.push({
      name: menu.name,
      label: menu.name,
      path: menu.path
    });
    
    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      menu.children.forEach(subMenu => traverseMenu(subMenu));
    }
  }
  
  menuList.forEach(menu => traverseMenu(menu));
  return permissions;
}

// 创建Vuex store
export default new Vuex.Store({
  // 状态
  state: {
    // 用户信息
    userInfo: {
      name: '',
      avatar: '',
      phone: '',
      role: '', // 用户角色
      permissions: [] // 用户权限列表
    },
    // 是否登录
    isLogin: false,
    // 权限列表
    permissionList: [],
    // 菜单列表
    menuList: []
  },
  
  // 改变状态的方法
  mutations: {
    // 设置用户信息
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
      state.isLogin = true;
      // 保存用户信息到localStorage
      localStorage.setItem('userAccount', JSON.stringify(userInfo));
    },
    
    // 清除用户信息
    clearUserInfo(state) {
      state.userInfo = {
        name: '',
        avatar: '',
        phone: '',
        role: '',
        permissions: []
      };
      state.isLogin = false;
      // 清除localStorage中的用户信息
      localStorage.removeItem('userAccount');
    },
    
    // 更新用户头像
    updateUserAvatar(state, avatar) {
      state.userInfo.avatar = avatar;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置权限列表
    setPermissionList(state, permissions) {
      state.permissionList = permissions;
    },
    
    // 设置用户权限
    setUserPermissions(state, permissions) {
      state.userInfo.permissions = permissions;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置菜单列表
    setMenuList(state, menuList) {
      state.menuList = menuList;
    }
  },
  
  // 异步操作
  actions: {
    // 登录
    login({ commit }, loginData) {
      // 导入登录API
      return import('@/api/public.js').then(({ login }) => {
        return login(loginData).then(response => {
          // 检查响应状态
          if (response.code === 200) {
            // 登录成功，获取返回数据
            const { token, user, menu, permission } = response.data;
            
            // 将token存储到localStorage
            localStorage.setItem('token', token);
            
            // 处理用户信息
            const userInfo = {
              name: user.nickname || '管理员',
              avatar: user.avatar || '',
              phone: user.mobile,
              role: user.role,
              permissions: Object.keys(permission) // 将权限对象的键转换为数组
            };
            
            // 保存用户信息到store
            commit('setUserInfo', userInfo);
            // 设置用户权限
            commit('setUserPermissions', userInfo.permissions);
            // 处理菜单列表（优先用后端 menu 字段；若含已废弃父级/全平铺旧版，回落到客户端两级定义）
            const backendMenu = (menu && Object.values(menu)) || []
            const menuDef = menuIsValidTwoLevel(backendMenu) ? backendMenu : CLIENT_MENU
            const rawMenuList = buildMenuList(menuDef)
            // 只保留当前路由表中存在的菜单（后端未重启/旧缓存返回已删模块时兜底过滤；父级分组项跳过路由校验）
            const validPaths = new Set(router.options.routes.map(r => r.path))
            const menuList = rawMenuList.filter(m => m.isParent || validPaths.has(m.path))
            // 设置菜单列表
            commit('setMenuList', menuList);
            // 保存菜单和权限到localStorage
            localStorage.setItem('menuList', JSON.stringify(menuList));
            localStorage.setItem('permissionList', JSON.stringify(permission));
            
            return response;
          } else {
            // 登录失败，抛出错误
            throw new Error(response.msg || '登录失败');
          }
        });
      });
    },
    
    // 退出登录
    logout({ commit }) {
      // 清除用户信息
      commit('clearUserInfo');
      // 清除菜单列表
      commit('setMenuList', []);
      // 清除token
      localStorage.removeItem('token');
      // 清除localStorage中的菜单和权限
      localStorage.removeItem('menuList');
      localStorage.removeItem('permissionList');
    },
    
    // 初始化用户信息
    initUserInfo({ commit }) {
      // 从localStorage中获取用户信息
      const userAccount = localStorage.getItem('userAccount');
      if (userAccount) {
        try {
          const userInfo = JSON.parse(userAccount);
          commit('setUserInfo', userInfo);
        } catch (error) {
          console.error('Failed to parse userAccount from localStorage:', error);
          localStorage.removeItem('userAccount');
        }
      }
      
      // 从localStorage中获取菜单列表
      const menuListStr = localStorage.getItem('menuList');
      if (menuListStr) {
        try {
          const cachedMenuList = JSON.parse(menuListStr);
          // 过滤掉当前路由表中已不存在的菜单项（清除旧版本缓存里已删除模块的入口；父级分组项跳过路由校验）
          const validPaths = new Set(router.options.routes.map(r => r.path));
          // 旧缓存若「全平铺（无任何父级）或含废弃父级」，用客户端两级定义重建 → 登录/刷新菜单保持一致
          const sourceList = menuListIsValid(cachedMenuList) ? cachedMenuList : buildFallbackMenuList()
          const menuList = sourceList.filter(m => m.isParent || validPaths.has(m.path));
          commit('setMenuList', menuList);
          if (menuList.length !== cachedMenuList.length || !menuListIsValid(cachedMenuList)) {
            localStorage.setItem('menuList', JSON.stringify(menuList));
          }
        } catch (error) {
          console.error('Failed to parse menuList from localStorage:', error);
          localStorage.removeItem('menuList');
        }
      }
      
      // 从localStorage中获取权限列表
      const permissionListStr = localStorage.getItem('permissionList');
      if (permissionListStr) {
        try {
          const permissionList = JSON.parse(permissionListStr);
          commit('setPermissionList', permissionList);
        } catch (error) {
          console.error('Failed to parse permissionList from localStorage:', error);
          localStorage.removeItem('permissionList');
        }
      }
    },
    
    // 加载权限列表
    loadPermissionList({ commit, state }) {
      // 如果用户已登录且有菜单列表，直接使用菜单列表生成权限
      if (state.isLogin && state.menuList.length > 0) {
        // 从菜单列表中提取权限
        const permissions = extractPermissionsFromMenu(state.menuList);
        commit('setPermissionList', permissions);
        return Promise.resolve(permissions);
      }
      
      // 哨兵管理系统菜单 fallback（复用 CLIENT_MENU 单一来源）
      const mockMenuList = buildFallbackMenuList()

      // 权限键：页面通过 hasPermission(key) 控制按钮显隐
      const permissionKeys = [
        'home', 'monitor',
        'factory_view', 'factory_add', 'factory_edit', 'factory_delete',
        'device_view', 'device_bind', 'device_unbind', 'device_rebind', 'device_edit',
        'security_view', 'security_add', 'security_edit', 'security_delete',
        'alert_view', 'alert_handle',
        'data_center_view',
        'asset_view', 'asset_edit', 'asset_batch',
        'track_view',
        'behavior_view',
        'audit_view',
        'message_view', 'message_read',
        'report_view', 'report_export',
        'workorder_view', 'workorder_create', 'workorder_dispatch', 'workorder_process', 'workorder_close',
        'mine_view', 'mine_sub_add', 'mine_sub_edit', 'mine_sub_delete', 'enterprise_view', 'enterprise_edit'
      ];
      const mockPermissionList = permissionKeys.map((k) => ({ name: k, label: k, path: '/' + k }));

      commit('setMenuList', mockMenuList);
      commit('setUserPermissions', permissionKeys);
      commit('setPermissionList', mockPermissionList);
      return Promise.resolve(mockPermissionList);
    }
  },
  
  // 计算属性
  getters: {
    // 获取用户信息
    getUserInfo: state => state.userInfo,
    // 获取是否登录
    getIsLogin: state => state.isLogin,
    // 获取用户名
    getUserName: state => state.userInfo.name,
    // 获取用户头像
    getUserAvatar: state => state.userInfo.avatar,
    // 获取用户角色
    getUserRole: state => state.userInfo.role,
    // 获取用户权限列表
    getUserPermissions: state => state.userInfo.permissions,
    // 获取权限列表
    getPermissionList: state => state.permissionList,
    // 检查用户是否有某个权限
    hasPermission: state => permission => {
      return state.userInfo.permissions.includes(permission);
    },
    // 检查用户是否有多个权限中的任意一个
    hasAnyPermission: state => permissions => {
      return permissions.some(permission => state.userInfo.permissions.includes(permission));
    },
    // 检查用户是否有全部权限
    hasAllPermissions: state => permissions => {
      return permissions.every(permission => state.userInfo.permissions.includes(permission));
    }
  }
});
